import { useEffect, useState, useCallback, useRef } from 'react';
import type { HostUser } from '@admin-portal/host-contract';

export interface AdminSession {
  state: 'admin';
  user: HostUser;
  refresh: () => Promise<void>;
}

export type Session =
  | { state: 'loading' }
  | { state: 'anonymous' }
  | { state: 'not-admin'; lineUserId: string }
  | AdminSession;

export function useSession(): Session {
  const [state, setState] = useState<Session>({ state: 'loading' });

  // Distinguish "not signed in" from "backend unreachable".
  //
  // The edge answers 401 only when the session itself is missing or invalid, and 503 when web-svc
  // could not be reached. Treating the second as the first is what makes a slow backend look like
  // a logout: the admin gets the LINE login screen mid-task even though their session is fine.
  // On 503 (and on a network error) we stay in 'loading' and retry with backoff, so a backend
  // hiccup costs a few seconds of spinner instead of the session.
  const attempt = useRef(0);
  const timer = useRef<number | undefined>(undefined);
  // Schedule through a ref rather than naming fetchSession inside its own initialiser: the
  // closure would resolve fine at call time, but the indirection keeps it obviously safe.
  const again = useRef<() => void>(() => {});

  const fetchSession = useCallback(async () => {
    const retryLater = () => {
      attempt.current += 1;
      const wait = Math.min(1000 * 2 ** (attempt.current - 1), 15000);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => { again.current(); }, wait);
    };
    try {
      const res = await fetch('/api/auth/session', { credentials: 'include' });
      if (res.status === 401) {
        attempt.current = 0;
        setState({ state: 'anonymous' });
        return;
      }
      if (res.status === 503 || res.status === 502 || res.status === 504) {
        retryLater();
        return;
      }
      if (!res.ok) throw new Error(`session fetch failed: ${res.status}`);
      attempt.current = 0;
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        setState({ state: 'anonymous' });
        return;
      }
      const body = await res.json();
      if (!body.user) {
        setState({ state: 'anonymous' });
        return;
      }
      if (body.user.role !== 'admin') {
        setState({ state: 'not-admin', lineUserId: body.user.lineUserId });
        return;
      }
      setState({ state: 'admin', user: body.user, refresh: fetchSession });
    } catch (err) {
      // A thrown fetch is a transport failure, not proof of a missing session -- retry.
      console.error('[useSession]', err);
      retryLater();
    }
  }, []);

  again.current = () => { void fetchSession(); };

  useEffect(() => {
    void fetchSession();
    return () => window.clearTimeout(timer.current);
  }, [fetchSession]);

  return state;
}
